<template>
  <MetaTags v-if="metaInfo" :metaInfo="metaInfo" />
  <mobile-search-tabs></mobile-search-tabs>
  <div class="results">
    <filters
      v-show="notEmbedded"
      :class="`${selectedMobileTab}-mobile-tab-active`"
      :locationTotalCount="listingData?.metaData?.totalCount"
      :filters="filters"
    ></filters>
    <result-list
      v-if="listingData"
      :class="`${selectedMobileTab}-mobile-tab-active`"
      :listings="listings"
      :locationTotalCount="listingData?.metaData?.totalCount"
      :filters="filters"
      :isFetching="isFetching"
      @onEndReached="() => !isFetching && hasNextPage && fetchNextPage()"
    ></result-list>
  </div>
</template>
<script lang="ts">
import * as actions from '@/app/store/action-types';
import Filters from '@/app/components/filters/filters.vue';
import ResultList from '@/app/components/result-list/result-list.vue';
import config from '@/config';
import utils from '@/app/utils/utils';
import cities from '@/utils/cities-top';
import cityNames from '@/utils/cities-names';
import { computed, defineAsyncComponent, defineComponent, onServerPrefetch, reactive, ref, watch } from 'vue';
import { useInfiniteQuery, useQueryClient } from '@tanstack/vue-query';
import MetaTags from '@/app/components/MetaTags.vue';
import MobileSearchTabs from '@/app/components/filters/mobile-search-tabs.vue';
import { fetchListingsQuery, fetchListingsQueryKey } from '@/app/data/useListingsData';
import searchUtils, { regionTypeMapping } from '@/app/utils/search-utils';
import useSafeRoute from '@/app/composables/useSafeRoute';
import { useRouter } from 'vue-router';
import logger from '@/app/logger';
import { useFilterStore } from '@/app/pinia/filterStore';
import { getFeatureGroupById } from '@/config/featureGroups';
import useLocationNamesData, { getLocationNamesQueryKey, locationNamesQuery } from '@/app/data/useLocationNamesData';
import type {
  CityNameType,
  CountyNameType,
  DistrictNameType,
  RegionNameType,
  StreetNameType,
} from '@/types/schemaTypes';
import { buildQuery } from '@/utils/url';
import isSSR from '@/utils/isSSR';

export default defineComponent({
  name: 'results',
  setup() {
    const regions = ref([] as RegionNameType[]);
    const counties = ref([] as CountyNameType[]);
    const cities = ref([] as CityNameType[]);
    const districts = ref([] as DistrictNameType[]);
    const streets = ref([] as StreetNameType[]);
    const router = useRouter(); // For watching route changes
    const route = useSafeRoute();
    const filterStore = useFilterStore();

    // Get filters from URL or use previous filters if no query params found (as when navigating to various search pages)
    // Watch for filter changes and sync them to the filter store
    // Fetch listings based on filters
    const filters = reactive(searchUtils.getValidatedFiltersFromUrl(route.value.path, route.value.query));

    const locationFilters = computed(() => {
      const { cityId, countyId, regionId, streetId, districtId } = filters;
      return { cityId, countyId, regionId, streetId, districtId };
    });

    const {
      data: listingDataPages,
      fetchNextPage,
      hasNextPage,
      isFetching,
    } = useInfiniteQuery({
      queryKey: [fetchListingsQueryKey, filters],
      queryFn: (ctx) => fetchListingsQuery(ctx, filters),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        return lastPage.nextCursor;
      },
    });

    watch(route, (updatedRoute, oldRoute) => {
      logger.info('Search route updated: ', { oldRoute, updatedRoute });
      const previousFiltersToUse = searchUtils.dropNonFeatureGroupFilters(updatedRoute.path, filterStore.filters);
      if (
        (!updatedRoute.query || Object.keys(updatedRoute.query).length === 0) &&
        Object.keys(previousFiltersToUse).length > 0 &&
        updatedRoute.path !== oldRoute.path
      ) {
        logger.info('No query params found, using previous filters: ', { previousFiltersToUse });
        const validFilters = searchUtils.dropNonFeatureGroupFilters(route.value.path, previousFiltersToUse);
        const params = new URLSearchParams(buildQuery(validFilters));
        const nextUrl = route.value.path + (params ? `?${params}` : '');
        void router.replace(nextUrl);
      } else {
        const updatedFilters = reactive(searchUtils.getValidatedFiltersFromUrl(updatedRoute.path, updatedRoute.query));
        utils.updateReactiveObject(filters, updatedFilters);
      }
    });

    watch(
      filters,
      (updatedFilters) => {
        filterStore.setFilters(updatedFilters);
      },
      { immediate: !isSSR } // This is to fill the store with filters on client side. Could also be done by (de)hydrating Pinia from server to client
    );

    // Get names for locations (cities, counties, regions, streets, districts)
    const locationNamesEnabled = computed(() => Object.values(locationFilters.value).some((v) => !!v));
    const { data: locationNames } = useLocationNamesData(locationFilters, locationNamesEnabled);
    watch(
      locationNames,
      (result) => {
        regions.value = result
          ? (result.filter((item) => item?.regionType === regionTypeMapping.region) as RegionNameType[])
          : [];
        counties.value = result
          ? (result.filter((item) => item?.regionType === regionTypeMapping.county) as CountyNameType[])
          : [];
        cities.value = result
          ? (result.filter((item) => item?.regionType === regionTypeMapping.city) as CityNameType[])
          : [];
        districts.value = result
          ? (result.filter((item) => item?.regionType === regionTypeMapping.district) as DistrictNameType[])
          : [];
        streets.value = result
          ? (result.filter((item) => item?.regionType === regionTypeMapping.street) as StreetNameType[])
          : [];
      },
      { immediate: true, flush: 'sync' } // Ensure watch is triggered on server side and immediately on client side
    );

    // Ensure necessary queries are done before rendering
    onServerPrefetch(async () => {
      const queryClient = useQueryClient();
      const prefetchPromises = [
        queryClient.prefetchQuery({
          queryKey: [fetchListingsQueryKey, filters],
          queryFn: fetchListingsQuery,
        }),
      ];

      if (locationNamesEnabled.value) {
        prefetchPromises.push(
          queryClient.prefetchQuery({
            queryKey: getLocationNamesQueryKey(locationFilters),
            queryFn: () => locationNamesQuery(locationFilters.value),
          })
        );
      }

      await Promise.all(prefetchPromises);
    });

    return {
      listingDataPages,
      filters,
      isFetching,
      fetchNextPage,
      hasNextPage,
      regionNames: locationNames,
      regions,
      counties,
      cities,
      districts,
      streets,
      route,
    };
  },
  data: () => ({
    selectedMobileTab: config.MOBILE_TABS.LIST,
    isEmbedded: false,
    isSuccess: false,
    isError: false,
  }),
  methods: {
    getAnalyticsPageName() {
      const analyticsTitle = ['haku'];
      const pathParts = utils.splitPathToParts(this.route.path);
      analyticsTitle.push(this.filters.mode === 'rental' ? 'vuokra' : 'myynti');
      analyticsTitle.push(pathParts[1]);
      if (pathParts[2]) {
        analyticsTitle.push(pathParts[2]);
      }
      return analyticsTitle.join(':');
    },
    initAnalytics() {
      void this.$store.dispatch(actions.analytics.set, {
        pageName: this.getAnalyticsPageName(),
        eVar17: this.title,
      });

      void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.pageView);
    },
    closeModal() {
      this.isSuccess = false;
      this.isError = false;
      this.$bus.$emit('closeDialogue');
    },
  },
  mounted() {
    this.$bus.$on(
      'search-selectedMobileTab',
      (tabName) => {
        this.selectedMobileTab = tabName;
      },
      this
    );
    this.initAnalytics();
  },
  watch: {
    route(to, from) {
      // compare paths to see if we "changed pages" (e.g. moved from /myytavat/asunnot to /myytavat/tontit)
      if (from.path !== to.path) {
        void this.$store.dispatch(actions.analytics.set, {
          pageName: this.getAnalyticsPageName(),
          eVar17: this.title,
        });
        void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.pageView);
      }
    },
    mode() {
      this.initAnalytics();
    },
  },
  computed: {
    listingData() {
      return this.listingDataPages?.pages[0];
    },
    listings() {
      const listings = this.listingDataPages ? this.listingDataPages.pages.flatMap((page) => page.listings) : [];
      return listings;
    },
    metaInfo() {
      const metaInfo = {
        title: this.title,
        meta: [{ vmid: 'description', name: 'description', content: '' }],
        script: [],
        link: [
          {
            rel: 'canonical',
            href: `${config.FRONT.PROD_BASE_URL}${this.route.path}`,
          },
        ],
      };

      let cityPart = '';
      const pathParts = utils.splitPathToParts(this.route.path);

      // If we have /cityname in the path, add canonical tag to metainfo and resolve city name used in description text
      if (pathParts.length === 3 && pathParts[2].length) {
        if (Object.keys(cities).some((city) => utils.nameToPathFormat(city) === pathParts[2])) {
          const name = Object.keys(cityNames).find((city) => utils.nameToPathFormat(city) === pathParts[2]);
          if (name) {
            cityPart = `${cityNames[name as keyof typeof cityNames]} `;
          }
        }
      }
      const modePart = this.filters.mode === 'rental' ? 'vuokrattavat' : 'myytävät';
      const typePart = getFeatureGroupById(this.filters.featureGroup)?.name.toLowerCase();
      const suffix = typePart === 'asunnot' ? 'unelmiesi koti' : 'tarpeisiisi sopiva kohde';
      metaInfo.meta[0].content = `Katso kaikki OP Kotien ${modePart} ${typePart} ${cityPart}helposti yhdestä paikasta. Meiltä saattaa löytyä juuri sinun ${suffix}. Tervetuloa tutustumaan!`;

      return metaInfo;
    },
    title() {
      const serviceName = '- OP Koti';
      const modePart = this.filters.mode === 'rental' ? 'Vuokrattavat' : 'Myytävät';
      const typePart = getFeatureGroupById(this.filters.featureGroup)?.name.toLowerCase();
      let title = `${modePart} ${typePart}`;
      const locationParts = [];
      if (this.cities.length) locationParts.push(this.cities.map((city) => city.city).join(', '));
      if (this.districts.length) locationParts.push(this.districts.map((district) => district.district).join(', '));
      if (this.streets.length) locationParts.push(this.streets.map((street) => street.street).join(', '));
      if (this.regions.length) locationParts.push(this.regions.map((region) => region.region).join(', '));
      if (this.filters.postalCode?.length) locationParts.push(this.filters.postalCode.join(', '));

      if (locationParts.length) {
        title = `${title} ${locationParts.join(', ')}`;
      }
      title = `${title} ${serviceName}`;
      return title;
    },
    notEmbedded() {
      return !this.isEmbedded;
    },
  },
  components: {
    Filters,
    ResultList,
    FormButton: defineAsyncComponent(() => import('@/app/components/form-button/form-button.vue')),
    MetaTags,
    MobileSearchTabs,
  },
});
</script>
